/**
 * CESM OSINT - Console de Commande Rapport
 * Interface TERMINAL / CLI militaire pour generer des rapports
 * 
 * Style: prompt de commande, texte monospace, pas de bulles
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Terminal, Download, ExternalLink, Copy, Loader2, 
  CheckCircle, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = {
  TYPE: 'type',
  THEATER: 'theater',
  MAGIC_OPTIONS: 'magic_options',
  DETAIL: 'detail',
  RECIPIENT: 'recipient',
  CONFIRM: 'confirm',
  GENERATING: 'generating',
  RESULT: 'result',
  ERROR: 'error',
};

const THEATERS = [
  { id: 'hormuz', label: "Detroit d'Ormuz", key: '1' },
  { id: 'red_sea', label: 'Mer Rouge', key: '2' },
  { id: 'south_china_sea', label: 'Mer de Chine', key: '3' },
  { id: 'black_sea', label: 'Mer Noire', key: '4' },
];

const DETAIL_LEVELS = [
  { id: 'executive', label: 'Executif (8 slides)', key: '1' },
  { id: 'strategic', label: 'Strategique (12 slides)', key: '2' },
  { id: 'technical', label: 'Technique (18 slides)', key: '3' },
];

const MAGIC_SECTIONS = [
  { id: 'news_analysis', label: 'Analyse de presse (OSINT)', default: true },
  { id: 'energy_markets', label: 'Marches energetiques (EIA/Brent)', default: true },
  { id: 'monte_carlo', label: 'Simulations Monte Carlo', default: true },
  { id: 'firms_thermal', label: 'Detections thermiques (FIRMS)', default: true },
  { id: 'adsb_flights', label: 'Trafic aerien militaire (ADS-B)', default: false },
  { id: 'telegram_intel', label: 'Renseignement Telegram', default: true },
  { id: 'satellite_data', label: 'Donnees satellites (TLE/ISR)', default: false },
  { id: 'propaganda_score', label: 'Score de propagande IA', default: false },
  { id: 'socmint_reddit', label: 'SOCMINT (Reddit)', default: false },
  { id: 'conflict_events', label: 'Evenements de conflit (GDELT)', default: true },
];

function TermLine({ type = 'output', children, prompt = 'CESM@RAPPORT' }) {
  if (type === 'input') {
    return (
      <div className="flex items-start gap-0 font-mono text-xs leading-relaxed">
        <span className="text-green-500 shrink-0">{prompt}&gt; </span>
        <span className="text-green-300">{children}</span>
      </div>
    );
  }
  if (type === 'system') {
    return (
      <div className="font-mono text-xs leading-relaxed text-cyan-400">
        [SYSTEM] {children}
      </div>
    );
  }
  if (type === 'error') {
    return (
      <div className="font-mono text-xs leading-relaxed text-red-400">
        [ERREUR] {children}
      </div>
    );
  }
  if (type === 'success') {
    return (
      <div className="font-mono text-xs leading-relaxed text-green-400">
        [OK] {children}
      </div>
    );
  }
  if (type === 'progress') {
    return (
      <div className="font-mono text-xs leading-relaxed text-amber-400/80">
        ... {children}
      </div>
    );
  }
  if (type === 'separator') {
    return <div className="font-mono text-xs text-white/20 select-none">{'─'.repeat(72)}</div>;
  }
  // default output
  return (
    <div className="font-mono text-xs leading-relaxed text-white/70">
      {children}
    </div>
  );
}

export default function ReportConsole({ token }) {
  const [step, setStep] = useState(STEPS.TYPE);
  const [lines, setLines] = useState([]);
  const [choices, setChoices] = useState({
    type: null,
    theater: 'hormuz',
    detail: 'strategic',
    recipient: null,
  });
  const [magicSections, setMagicSections] = useState(
    Object.fromEntries(MAGIC_SECTIONS.map(s => [s.id, s.default]))
  );
  const [recipients, setRecipients] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const initialized = useRef(false);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const addLine = useCallback((type, text) => {
    setLines(prev => [...prev, { type, text, time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }]);
  }, []);

  // Auto-scroll + focus
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [lines]);

  useEffect(() => {
    if (!isGenerating) inputRef.current?.focus();
  }, [step, isGenerating, lines]);

  // Load recipients
  useEffect(() => {
    if (token) {
      axios.get(`${API_URL}/api/report-recipients`, authHeaders)
        .then(res => setRecipients(res.data.recipients || []))
        .catch(() => {});
    }
  }, [token]);

  // Boot message
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      setLines([
        { type: 'separator' },
        { type: 'system', text: 'CESM OSINT v4.1 — Module de Generation de Rapports' },
        { type: 'system', text: 'Analyste : ARGOS (OIA-1). Tapez le numero de votre choix ou une commande.' },
        { type: 'separator' },
        { type: 'output', text: '' },
        { type: 'output', text: 'Quel type de rapport souhaitez-vous generer ?' },
        { type: 'output', text: '' },
        { type: 'output', text: '  [1]  MAGIC    — Rapport PDF 30+ pages, analyse complete automatisee' },
        { type: 'output', text: '  [2]  GAMMA    — Presentation slides via Gamma.app' },
        { type: 'output', text: '' },
        { type: 'output', text: 'Commandes: help, status, history, clear' },
      ]);
    }
  }, []);

  const showTypeMenu = () => {
    addLine('separator');
    addLine('output', 'Quel type de rapport souhaitez-vous generer ?');
    addLine('output', '');
    addLine('output', '  [1]  MAGIC    — Rapport PDF 30+ pages, analyse complete automatisee');
    addLine('output', '  [2]  GAMMA    — Presentation slides via Gamma.app');
    addLine('output', '');
    addLine('output', 'Commandes: help, status, history, worldmon, clear');
    setStep(STEPS.TYPE);
  };

  const showTheaterMenu = () => {
    addLine('output', '');
    addLine('output', 'Selectionnez le theatre d\'operations :');
    addLine('output', '');
    THEATERS.forEach(t => addLine('output', `  [${t.key}]  ${t.label}`));
    addLine('output', '');
    setStep(STEPS.THEATER);
  };

  const showMagicOptionsMenu = () => {
    addLine('output', '');
    addLine('system', 'SECTIONS DU RAPPORT — Cochez/decochez les modules a inclure');
    addLine('output', '');
    setStep(STEPS.MAGIC_OPTIONS);
  };

  const showDetailMenu = () => {
    addLine('output', '');
    addLine('output', 'Niveau de detail de la presentation :');
    addLine('output', '');
    DETAIL_LEVELS.forEach(d => addLine('output', `  [${d.key}]  ${d.label}`));
    addLine('output', '');
    setStep(STEPS.DETAIL);
  };

  const showRecipientMenu = () => {
    addLine('output', '');
    addLine('output', 'Destinataire du rapport (optionnel) :');
    addLine('output', '');
    recipients.forEach((r, i) => addLine('output', `  [${i + 1}]  ${r.name}${r.role ? ' — ' + r.role : ''}`));
    addLine('output', `  [0]  Sans destinataire specifique`);
    addLine('output', '');
    setStep(STEPS.RECIPIENT);
  };

  const showConfirm = () => {
    const theaterLabel = THEATERS.find(t => t.id === choices.theater)?.label || choices.theater;
    const detailLabel = DETAIL_LEVELS.find(d => d.id === choices.detail)?.label || choices.detail;
    
    addLine('separator');
    addLine('system', 'RECAPITULATIF');
    addLine('output', '');
    if (choices.type === 'magic') {
      addLine('output', `  Type ........... Magic Report (PDF)`);
      addLine('output', `  Theatre ........ ${theaterLabel}`);
      const selectedSections = MAGIC_SECTIONS.filter(s => magicSections[s.id]).map(s => s.label);
      addLine('output', `  Sections ....... ${selectedSections.length} modules actifs`);
      selectedSections.forEach(s => addLine('output', `                   + ${s}`));
    } else {
      addLine('output', `  Type ........... Presentation Gamma`);
      addLine('output', `  Detail ......... ${detailLabel}`);
    }
    addLine('output', `  Destinataire ... ${choices.recipient || 'Aucun'}`);
    addLine('output', '');
    addLine('output', 'Confirmer la generation ?  [Y] Oui  [N] Annuler');
    addLine('output', '');
    setStep(STEPS.CONFIRM);
  };

  const launchGeneration = async () => {
    setIsGenerating(true);
    setStep(STEPS.GENERATING);
    addLine('separator');
    addLine('system', 'GENERATION EN COURS...');
    addLine('output', '');

    const recipientProfile = recipients.find(r => r.name === choices.recipient);
    const prefs = recipientProfile ? {
      recipient_name: recipientProfile.name,
      recipient_preferences: recipientProfile.preferences,
      detail_level: recipientProfile.detail_level
    } : {};

    try {
      if (choices.type === 'magic') {
        addLine('progress', 'Collecte des donnees OSINT en cours...');
        addLine('progress', 'Interrogation EIA, GDELT, FIRMS, Telegram...');
        addLine('progress', 'Execution simulations Monte Carlo (500 iterations)...');
        addLine('progress', 'Generation du rapport PDF (~2-3 min)...');

        const response = await axios.post(`${API_URL}/api/reports/magic`, {
          theater_id: choices.theater,
          monte_carlo_iterations: 500,
          ...prefs
        }, { ...authHeaders, timeout: 300000 });

        if (response.data.status === 'success') {
          const stats = response.data.statistics || {};
          addLine('output', '');
          addLine('success', 'RAPPORT GENERE AVEC SUCCES');
          addLine('output', '');
          addLine('output', `  Articles analyses .. ${stats.news_analyzed || 0}`);
          addLine('output', `  Simulations ........ ${stats.simulations_run || 0}`);
          addLine('output', `  Score EWS .......... ${stats.ews_score || 0}/100 (${stats.ews_code || 'N/A'})`);
          addLine('output', `  Fichier ............ ${response.data.filename || 'magic_report.pdf'}`);
          addLine('output', '');
          addLine('output', 'Tapez [D] pour telecharger le PDF.');
          setResult({ type: 'magic', url: `${API_URL}${response.data.url}`, filename: response.data.filename, stats });
          setStep(STEPS.RESULT);
        } else {
          throw new Error(response.data.message || 'Erreur generation');
        }
      } else {
        addLine('progress', 'Collecte des donnees OSINT...');
        addLine('progress', 'Envoi a Gamma.app...');

        const response = await axios.post(`${API_URL}/api/reports/gamma/generate`, {
          report_type: choices.detail,
          ...prefs
        }, { ...authHeaders, timeout: 120000 });

        if (response.data.status === 'success') {
          addLine('output', '');
          addLine('success', 'PRESENTATION GAMMA CREEE');
          addLine('output', '');
          addLine('output', `  URL .. ${response.data.gamma_url}`);
          addLine('output', '');
          addLine('output', 'Tapez [O] pour ouvrir, [C] pour copier le lien.');
          setResult({ type: 'gamma', url: response.data.gamma_url, generationId: response.data.generation_id });
          setStep(STEPS.RESULT);
        } else {
          throw new Error(response.data.message || response.data.details || 'Erreur API Gamma');
        }
      }
    } catch (err) {
      const errMsg = err.code === 'ECONNABORTED'
        ? 'Timeout — le rapport prend plus de 5 minutes'
        : (err.response?.data?.detail || err.response?.data?.message || err.message || 'Erreur inconnue');
      addLine('output', '');
      addLine('error', errMsg);
      addLine('output', '');
      addLine('output', 'Tapez [R] pour reessayer ou [N] pour un nouveau rapport.');
      setStep(STEPS.ERROR);
      toast.error(`Erreur: ${errMsg}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPdf = async () => {
    if (!result?.url) return;
    try {
      addLine('progress', 'Telechargement en cours...');
      const response = await axios.get(result.url, { ...authHeaders, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename || `magic_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      addLine('success', 'PDF telecharge.');
    } catch {
      addLine('error', 'Fichier non disponible. Regenerez le rapport.');
    }
  };

  const handleCommand = (raw) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    
    addLine('input', raw.trim());

    // Global commands
    if (cmd === 'help') {
      addLine('output', '');
      addLine('system', 'ARGOS (OIA-1) — Commandes disponibles :');
      addLine('output', '  help      Afficher cette aide');
      addLine('output', '  status    Etat du systeme');
      addLine('output', '  clear     Effacer la console');
      addLine('output', '  new       Nouveau rapport');
      addLine('output', '  history   Voir l\'historique');
      addLine('output', '  worldmon  Ouvrir WorldMonitor');
      addLine('output', '  prompts   Ouvrir la bibliotheque OSINT');
      addLine('output', '  carte     Ouvrir la carte tactique');
      addLine('output', '');
      addLine('output', 'Email : envoyez une question a analyste@osint-marine.fr');
      addLine('output', '');
      return;
    }
    if (cmd === 'clear') {
      setLines([]);
      addLine('system', 'Console effacee.');
      showTypeMenu();
      return;
    }
    if (cmd === 'new' || (cmd === 'n' && step !== STEPS.CONFIRM && step !== STEPS.ERROR && step !== STEPS.RESULT)) {
      setChoices({ type: null, theater: 'hormuz', detail: 'strategic', recipient: null });
      setMagicSections(Object.fromEntries(MAGIC_SECTIONS.map(s => [s.id, s.default])));
      setResult(null);
      showTypeMenu();
      return;
    }
    if (cmd === 'status') {
      addLine('output', '');
      addLine('system', `CESM OSINT v3.1 — ${new Date().toLocaleString('fr-FR')}`);
      addLine('output', `  Sources OSINT .. LIVE`);
      addLine('output', `  Base MongoDB ... CONNECTED`);
      addLine('output', `  Claude Sonnet .. READY`);
      addLine('output', `  Gamma.app ...... READY`);
      addLine('output', `  WorldMonitor ... LINKED`);
      addLine('output', '');
      return;
    }
    if (cmd === 'worldmon' || cmd === 'wm') {
      addLine('system', 'Ouverture de WorldMonitor...');
      window.open('https://worldmonitor.app', '_blank');
      return;
    }
    if (cmd === 'history') {
      addLine('progress', 'Chargement de l\'historique...');
      axios.get(`${API_URL}/api/reports/magic/history`, authHeaders)
        .then(res => {
          const reports = res.data.reports || [];
          addLine('output', '');
          addLine('system', `${reports.length} Magic Report(s) en archive`);
          reports.slice(0, 5).forEach(r => {
            addLine('output', `  ${new Date(r.created_at).toLocaleString('fr-FR')} | EWS ${r.statistics?.ews_score || '?'} | ${r.statistics?.news_analyzed || '?'} articles`);
          });
          addLine('output', '');
        })
        .catch(() => addLine('error', 'Impossible de charger l\'historique.'));
      return;
    }

    // Step-specific handling
    switch (step) {
      case STEPS.TYPE:
        if (cmd === '1' || cmd === 'magic') {
          setChoices(c => ({ ...c, type: 'magic' }));
          addLine('success', 'Type: Magic Report (PDF)');
          showTheaterMenu();
        } else if (cmd === '2' || cmd === 'gamma') {
          setChoices(c => ({ ...c, type: 'gamma' }));
          addLine('success', 'Type: Presentation Gamma');
          showDetailMenu();
        } else {
          addLine('error', `Commande inconnue: "${raw.trim()}". Tapez 1 ou 2.`);
        }
        break;

      case STEPS.THEATER: {
        const theater = THEATERS.find(t => t.key === cmd || t.id === cmd);
        if (theater) {
          setChoices(c => ({ ...c, theater: theater.id }));
          addLine('success', `Theatre: ${theater.label}`);
          showMagicOptionsMenu();
        } else {
          addLine('error', `Option invalide. Tapez un numero de 1 a ${THEATERS.length}.`);
        }
        break;
      }

      case STEPS.MAGIC_OPTIONS:
        if (cmd === 'ok' || cmd === 'done' || cmd === 'valider') {
          const count = MAGIC_SECTIONS.filter(s => magicSections[s.id]).length;
          addLine('success', `${count} modules selectionnes`);
          showRecipientMenu();
        } else {
          addLine('error', 'Utilisez les cases a cocher ci-dessus, puis tapez OK pour valider.');
        }
        break;

      case STEPS.DETAIL:
        const detail = DETAIL_LEVELS.find(d => d.key === cmd || d.id === cmd);
        if (detail) {
          setChoices(c => ({ ...c, detail: detail.id }));
          addLine('success', `Detail: ${detail.label}`);
          showRecipientMenu();
        } else {
          addLine('error', `Option invalide. Tapez un numero de 1 a ${DETAIL_LEVELS.length}.`);
        }
        break;

      case STEPS.RECIPIENT:
        if (cmd === '0') {
          setChoices(c => ({ ...c, recipient: null }));
          addLine('success', 'Destinataire: aucun');
          showConfirm();
        } else {
          const idx = parseInt(cmd) - 1;
          if (idx >= 0 && idx < recipients.length) {
            setChoices(c => ({ ...c, recipient: recipients[idx].name }));
            addLine('success', `Destinataire: ${recipients[idx].name}`);
            showConfirm();
          } else {
            addLine('error', `Option invalide. Tapez 0 pour aucun ou un numero valide.`);
          }
        }
        break;

      case STEPS.CONFIRM:
        if (cmd === 'y' || cmd === 'yes' || cmd === 'oui' || cmd === 'o') {
          launchGeneration();
        } else if (cmd === 'n' || cmd === 'no' || cmd === 'non') {
          addLine('system', 'Generation annulee.');
          setChoices({ type: null, theater: 'hormuz', detail: 'strategic', recipient: null });
          showTypeMenu();
        } else {
          addLine('error', 'Tapez Y pour confirmer ou N pour annuler.');
        }
        break;

      case STEPS.RESULT:
        if (cmd === 'd' || cmd === 'download') {
          downloadPdf();
        } else if (cmd === 'o' || cmd === 'open') {
          if (result?.url) window.open(result.url, '_blank');
        } else if (cmd === 'c' || cmd === 'copy') {
          if (result?.url) { navigator.clipboard.writeText(result.url); addLine('success', 'Lien copie dans le presse-papiers.'); }
        } else if (cmd === 'n' || cmd === 'new') {
          setResult(null);
          setChoices({ type: null, theater: 'hormuz', detail: 'strategic', recipient: null });
          showTypeMenu();
        } else {
          addLine('error', `Commande: D=telecharger, O=ouvrir, C=copier, N=nouveau`);
        }
        break;

      case STEPS.ERROR:
        if (cmd === 'r' || cmd === 'retry') {
          launchGeneration();
        } else if (cmd === 'n' || cmd === 'new') {
          setResult(null);
          setChoices({ type: null, theater: 'hormuz', detail: 'strategic', recipient: null });
          showTypeMenu();
        } else {
          addLine('error', 'Tapez R pour reessayer ou N pour un nouveau rapport.');
        }
        break;

      default:
        addLine('error', `Commande inconnue: "${raw.trim()}". Tapez help.`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isGenerating) {
      handleCommand(inputValue);
      setInputValue('');
    }
  };

  return (
    <div
      className="bg-[#0c0c0c] border border-green-900/50 rounded overflow-hidden flex flex-col"
      style={{ height: '55vh', maxHeight: 520, fontFamily: "'Courier New', 'Lucida Console', monospace" }}
      data-testid="report-console"
      onClick={() => inputRef.current?.focus()}
    >
      {/* Title bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#1a1a1a] border-b border-green-900/40">
        <div className="flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5 text-green-500" />
          <span className="text-[11px] text-green-500 font-bold tracking-wider">CESM@RAPPORT</span>
          <span className="text-[10px] text-white/20">—</span>
          <span className="text-[10px] text-white/30">
            {isGenerating ? 'Generation en cours...' : step === STEPS.RESULT ? 'Rapport pret' : 'En attente'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isGenerating && <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />}
          <button
            onClick={(e) => { e.stopPropagation(); handleCommand('new'); }}
            className="text-[10px] text-white/30 hover:text-green-400 border border-white/10 px-1.5 py-0.5 rounded hover:border-green-500/40 transition-all"
            data-testid="console-new"
          >
            NEW
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleCommand('clear'); }}
            className="text-[10px] text-white/30 hover:text-green-400 border border-white/10 px-1.5 py-0.5 rounded hover:border-green-500/40 transition-all"
            data-testid="console-clear"
          >
            CLR
          </button>
        </div>
      </div>

      {/* Terminal output */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5 bg-[#0c0c0c]">
        {lines.map((line, i) => (
          <TermLine key={i} type={line.type}>{line.text}</TermLine>
        ))}

        {/* Magic Report sections checkboxes */}
        {step === STEPS.MAGIC_OPTIONS && (
          <div className="py-2 space-y-1" data-testid="magic-options">
            {MAGIC_SECTIONS.map(section => (
              <label
                key={section.id}
                className="flex items-center gap-2 px-2 py-1 hover:bg-green-900/20 cursor-pointer rounded group"
              >
                <input
                  type="checkbox"
                  checked={magicSections[section.id]}
                  onChange={() => setMagicSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                  className="w-3.5 h-3.5 accent-green-500 cursor-pointer"
                  data-testid={`magic-opt-${section.id}`}
                />
                <span className={`font-mono text-xs ${magicSections[section.id] ? 'text-green-400' : 'text-white/40'} group-hover:text-green-300 transition-colors`}>
                  {section.label}
                </span>
              </label>
            ))}
            <div className="font-mono text-xs text-white/30 pt-1 pl-2">
              Tapez <span className="text-green-500">OK</span> pour valider la selection
            </div>
          </div>
        )}

        {/* Quick action buttons for result */}
        {step === STEPS.RESULT && result && !isGenerating && (
          <div className="flex gap-2 pt-2 pb-1">
            {result.type === 'magic' ? (
              <button
                onClick={() => downloadPdf()}
                className="px-3 py-1.5 bg-green-900/30 border border-green-500/50 text-green-400 text-xs font-mono flex items-center gap-1.5 hover:bg-green-900/50 transition-all"
                data-testid="console-download-pdf"
              >
                <Download className="w-3 h-3" /> [D] Telecharger PDF
              </button>
            ) : (
              <>
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-green-900/30 border border-green-500/50 text-green-400 text-xs font-mono flex items-center gap-1.5 hover:bg-green-900/50 transition-all"
                  data-testid="console-open-gamma"
                >
                  <ExternalLink className="w-3 h-3" /> [O] Ouvrir
                </a>
                <button
                  onClick={() => { navigator.clipboard.writeText(result.url); addLine('success', 'Lien copie.'); }}
                  className="px-3 py-1.5 bg-green-900/30 border border-green-500/50 text-green-400 text-xs font-mono flex items-center gap-1.5 hover:bg-green-900/50 transition-all"
                  data-testid="console-copy-link"
                >
                  <Copy className="w-3 h-3" /> [C] Copier
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Input prompt */}
      <div className="border-t border-green-900/40 bg-[#0f0f0f] px-3 py-2 flex items-center gap-0">
        <span className="text-green-500 text-xs font-mono shrink-0">CESM@RAPPORT&gt; </span>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isGenerating}
          placeholder={isGenerating ? 'Veuillez patienter...' : ''}
          className="flex-1 bg-transparent text-green-300 text-xs font-mono outline-none border-none placeholder:text-green-900 caret-green-400 disabled:opacity-30"
          autoComplete="off"
          spellCheck={false}
          data-testid="console-input"
        />
        {isGenerating && <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin ml-2" />}
      </div>
    </div>
  );
}
